// Firebase app + Remote Config bootstrap for the web admin/client portal.
//
// Config is read from REACT_APP_FIREBASE_* env vars (CRA only exposes vars
// with that prefix to the client bundle). The dedicated web project is
// `moblyze-admin-portal` — separate from the mobile flavors so flags and
// targeting conditions are managed independently.
//
// Everything here degrades gracefully: if the Firebase env is missing (e.g. a
// local checkout without secrets, or a test run) `getRemoteConfigInstance()`
// returns null and the feature-flag layer falls back to its DEFAULTS. The app
// must never fail to boot just because Remote Config is unavailable.

import { initializeApp } from "firebase/app";
import { getRemoteConfig } from "firebase/remote-config";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// The minimum needed to talk to Remote Config.
function hasFirebaseConfig() {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.appId,
  );
}

let warnedMissingConfig = false;
let remoteConfigInstance;

// Lazily initialise so importing this module (e.g. in tests / jsdom) never
// throws and we only touch the SDK once a browser context actually asks for it.
export function getRemoteConfigInstance() {
  if (remoteConfigInstance !== undefined) return remoteConfigInstance;

  if (typeof window === "undefined" || !hasFirebaseConfig()) {
    if (!warnedMissingConfig) {
      warnedMissingConfig = true;
      // eslint-disable-next-line no-console
      console.warn(
        "[featureFlags] Firebase Remote Config not configured " +
          "(missing REACT_APP_FIREBASE_* env) — using flag defaults.",
      );
    }
    remoteConfigInstance = null;
    return remoteConfigInstance;
  }

  try {
    const app = initializeApp(firebaseConfig);
    remoteConfigInstance = getRemoteConfig(app);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[featureFlags] Failed to initialise Remote Config", err);
    remoteConfigInstance = null;
  }
  return remoteConfigInstance;
}
